import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import { useAuthStore } from '@/stores/authStore'
import type { Branch } from '@/types/api'

/**
 * WebSocket/STOMP configuration and client management.
 *
 * This mirrors the v1 ws.js implementation using modern @stomp/stompjs.
 * Key features:
 * - SockJS for WebSocket fallback
 * - STOMP protocol for message routing
 * - Automatic reconnection
 * - Subscription management
 */

const BASE_DOMAIN = 'alpha-trader.com'

// Environment-aware logging - only logs in development
const wsLog = {
  log: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.log('[WebSocket]', ...args)
  },
  warn: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.warn('[WebSocket]', ...args)
  },
  error: (...args: unknown[]) => {
    // Errors are always logged (useful for debugging production issues)
    console.error('[WebSocket]', ...args)
  },
}

// Singleton STOMP client
let stompClient: Client | null = null

// Subscription registry
const subscriptions = new Map<string, StompSubscription>()

// Reconnect callbacks
const reconnectCallbacks: Array<() => void> = []

// Connection state
let isConnected = false
let isConnecting = false

/**
 * Get the WebSocket URL for the given branch.
 */
function getWsUrl(branch: Branch): string {
  // In development, connect directly to the backend
  if (import.meta.env.DEV) {
    return `https://${branch}.${BASE_DOMAIN}/ws`
  }
  return `https://${branch}.${BASE_DOMAIN}/ws`
}

/**
 * Initialize the STOMP client.
 */
export function initializeWebSocket(): Client {
  if (stompClient) {
    return stompClient
  }

  const { branch, getToken } = useAuthStore.getState()
  const wsUrl = getWsUrl(branch)
  const token = getToken()

  stompClient = new Client({
    // Use SockJS for WebSocket with fallback
    webSocketFactory: () => new SockJS(wsUrl) as WebSocket,

    // Auth headers
    connectHeaders: {
      Authorization: `Bearer ${token || ''}`,
    },

    // Reconnect settings
    reconnectDelay: 5000,

    // Heartbeat settings
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,

    // Connection handlers
    onConnect: () => {
      wsLog.log('Connected')
      isConnected = true
      isConnecting = false

      // Re-subscribe to all active subscriptions
      subscriptions.forEach((_sub, destination) => {
        wsLog.log(`Re-subscribing to ${destination}`)
      })

      // Call reconnect callbacks
      reconnectCallbacks.forEach((callback) => {
        try {
          callback()
        } catch (error) {
          wsLog.error('Reconnect callback error:', error)
        }
      })
    },

    onDisconnect: () => {
      wsLog.log('Disconnected')
      isConnected = false
    },

    onStompError: (frame) => {
      wsLog.error('STOMP error:', frame.headers.message)

      // Check for security errors (token invalid)
      if (frame.headers.message?.includes('SecurityException')) {
        wsLog.warn('Security error - redirecting to logout')
        // Could dispatch logout action here
      }
    },

    onWebSocketError: (error) => {
      wsLog.error('WebSocket error:', error)
      isConnected = false
    },
  })

  return stompClient
}

/**
 * Connect to the WebSocket server.
 */
export async function connectWebSocket(): Promise<void> {
  if (isConnected || isConnecting) {
    return
  }

  isConnecting = true
  const client = initializeWebSocket()

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('WebSocket connection timeout'))
    }, 10000)

    const originalOnConnect = client.onConnect
    client.onConnect = (frame) => {
      clearTimeout(timeout)
      originalOnConnect?.(frame)
      resolve()
    }

    const originalOnStompError = client.onStompError
    client.onStompError = (frame) => {
      clearTimeout(timeout)
      originalOnStompError?.(frame)
      reject(new Error(frame.headers.message || 'STOMP error'))
    }

    client.activate()
  })
}

/**
 * Disconnect from the WebSocket server.
 */
export async function disconnectWebSocket(): Promise<void> {
  if (stompClient) {
    // Unsubscribe from all subscriptions
    subscriptions.forEach((sub) => sub.unsubscribe())
    subscriptions.clear()

    await stompClient.deactivate()
    stompClient = null
    isConnected = false
  }
}

/**
 * Subscribe to a STOMP destination.
 */
export function subscribe(
  destination: string,
  callback: (message: IMessage) => void
): StompSubscription | null {
  if (!stompClient || !isConnected) {
    wsLog.warn(`Cannot subscribe to ${destination} - not connected`)
    return null
  }

  // Unsubscribe from existing subscription if any
  const existing = subscriptions.get(destination)
  if (existing) {
    existing.unsubscribe()
  }

  // Create new subscription
  const subscription = stompClient.subscribe(destination, callback)
  subscriptions.set(destination, subscription)

  return subscription
}

/**
 * Unsubscribe from a STOMP destination.
 */
export function unsubscribe(destination: string): void {
  const subscription = subscriptions.get(destination)
  if (subscription) {
    subscription.unsubscribe()
    subscriptions.delete(destination)
    wsLog.log(`Unsubscribed from ${destination}`)
  }
}

/**
 * Send a message to a STOMP destination.
 */
export function sendMessage(destination: string, body: string): void {
  if (!stompClient || !isConnected) {
    wsLog.warn(`Cannot send to ${destination} - not connected`)
    return
  }

  stompClient.publish({
    destination,
    body,
  })
}

/**
 * Register a callback to be called on reconnection.
 */
export function addReconnectCallback(callback: () => void): () => void {
  reconnectCallbacks.push(callback)

  // Return unregister function
  return () => {
    const index = reconnectCallbacks.indexOf(callback)
    if (index > -1) {
      reconnectCallbacks.splice(index, 1)
    }
  }
}

/**
 * Check if WebSocket is connected.
 */
export function isWebSocketConnected(): boolean {
  return isConnected
}

/**
 * Get the STOMP client instance.
 */
export function getStompClient(): Client | null {
  return stompClient
}

// WebSocket message ID counter (mirrors v1's websocketId)
let messageId = Math.floor(Math.random() * 1000000) + 1

/**
 * Get a unique message ID for request-response correlation.
 */
export function nextMessageId(): number {
  return ++messageId
}
